/**
 * Shared pieces for the media-sources citation generators (audiobooks, live
 * performances, webinars, online courses, comics, advertisements, exhibitions and AI
 * images).
 *
 * The general helpers come from `citation-parts.ts` (names, author lists, dates) and the
 * field, italic and title-case helpers from the legal-sources family's `common.ts`. What
 * is here is what those do not cover: a list of people typed one per line, the role
 * credits APA and MLA put after a title ("K. Mazur, Narr.", "illustrated by Nate
 * Powell"), date ranges written the way each style writes them, shortened titles for
 * in-text citations, and the possessive Chicago uses in an AI image credit.
 *
 * Words that belong to a citation format ("Narr.", "illustrated by", "et al.", month
 * names) are format data and stay in code; everything around a citation that a reader
 * sees as a label or a note comes from the widget's strings.
 */

import {
  type PartialDate,
  type PersonName,
  parsePersonName,
} from "@/lib/tool-kit/citation-parts";

/** Field values as the builder stores them. */
export type MediaValues = Readonly<Record<string, string | undefined>>;

/** The style labels and entry labels every generator in the family shares. */
export interface MediaLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  shortNote: string;
  bibliography: string;
}

const RE_LINE_BREAK = /\r?\n/;

/**
 * People typed one per line — "Mary Jane Smith" or "Smith, Mary Jane" — in order.
 * Blank lines are ignored.
 */
export function parsePeople(text: string): PersonName[] {
  const people: PersonName[] = [];
  for (const line of text.split(RE_LINE_BREAK)) {
    const person = parsePersonName(line);
    if (person) {
      people.push(person);
    }
  }
  return people;
}

/** A group (band, orchestra, museum) kept exactly as typed — never inverted. */
export function groupName(name: string): PersonName {
  return { given: [], family: name.trim(), organization: true };
}

/** "A", "A and B", "A, B, and C" — the serial comma all three styles use. */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) {
    return items.join("");
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
}

/** APA's ampersand list inside a role credit: "T. Fan & E. Fan", "A, B, & C". */
export function apaAmpersandList(items: readonly string[]): string {
  if (items.length <= 1) {
    return items.join("");
  }
  if (items.length === 2) {
    return `${items[0]} & ${items[1]}`;
  }
  return `${items.slice(0, -1).join(", ")}, & ${items.at(-1)}`;
}

/**
 * MLA 9's Contributor element: "illustrated by Nate Powell", "narrated by Cherise
 * Booth and Adenrele Ojo"; with three or more people in one role, the first name and
 * "et al." — the MLA Style Center's rule for comics collaborators, and the same rule
 * MLA applies to authors.
 */
export function mlaRoleNames(names: readonly string[]): string {
  if (names.length >= 3) {
    return `${names[0]} et al.`;
  }
  return andList(names);
}

/* ------------------------------------------------------------------ titles */

const RE_LEADING_ARTICLE = /^(the|a|an)\s+/i;

/** The title before any subtitle: "Unbound: Narrative Art" → "Unbound". */
export function beforeSubtitle(text: string): string {
  const colon = text.indexOf(":");
  return (colon > 0 ? text.slice(0, colon) : text).trim();
}

/**
 * MLA's shortened title for an in-text citation: the main title, without a subtitle
 * or a leading article — "(*Unbound*)" for *Unbound: Narrative Art of the Plains*.
 */
export function mlaShortTitle(text: string): string {
  return beforeSubtitle(text).replace(RE_LEADING_ARTICLE, "");
}

const RE_WORDS = /\s+/;

const RE_TRAILING_PUNCT = /[.,;:!?]+$/;

const MINOR_TAIL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
]);

/**
 * Chicago's shortened title for a shortened note: the main title without a leading
 * article — "Roy, *God of Small Things*" in the CMOS 18 Quick Guide — cut to four
 * words when it is longer, stopping before a small word once two key words are in
 * ("A History of the World in Six Glasses" → "History of the World", "Recovery
 * Support for Young People" → "Recovery Support"). CMOS picks key words by hand — its own
 * sample shortens a long lecture title to "Oliver, “Why.”" — so this is a starting
 * point to check, not a rule.
 */
export function chicagoShortTitle(text: string): string {
  const main = beforeSubtitle(text).replace(RE_LEADING_ARTICLE, "");
  const words = main.split(RE_WORDS).filter(Boolean);
  if (words.length <= 4) {
    return main;
  }
  const isMinor = (word: string) => MINOR_TAIL_WORDS.has(word.toLowerCase());
  const kept: string[] = [];
  let content = 0;
  for (const word of words) {
    if (kept.length === 4 || (isMinor(word) && content >= 2)) {
      break;
    }
    kept.push(word);
    content += isMinor(word) ? 0 : 1;
  }
  while (kept.length > 1 && isMinor(kept.at(-1) ?? "")) {
    kept.pop();
  }
  return kept.join(" ").replace(RE_TRAILING_PUNCT, "");
}

/**
 * The first few words of a long description or prompt, for an in-text citation that
 * keys to it: (“Create an expressionist-style image”). Trailing punctuation and
 * trailing small words are dropped: "A lighthouse on a basalt cliff" → "A lighthouse".
 */
export function firstWords(text: string, count: number): string {
  const words = text.trim().split(RE_WORDS).filter(Boolean).slice(0, count);
  while (
    words.length > 1 &&
    MINOR_TAIL_WORDS.has((words.at(-1) ?? "").toLowerCase())
  ) {
    words.pop();
  }
  return words.join(" ").replace(RE_TRAILING_PUNCT, "");
}

/* ------------------------------------------------------------------- dates */

const LONG_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** MLA 9 month abbreviations (MLA Handbook 9th ed., appendix 1). */
const MLA_MONTHS = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May",
  "June",
  "July",
  "Aug.",
  "Sept.",
  "Oct.",
  "Nov.",
  "Dec.",
];

function dayMonth(date: PartialDate, months: readonly string[]): string {
  if (!date.month) {
    return "";
  }
  const month = months[date.month - 1];
  return date.day ? `${date.day} ${month}` : month;
}

function monthDay(date: PartialDate): string {
  if (!date.month) {
    return "";
  }
  const month = LONG_MONTHS[date.month - 1];
  return date.day ? `${month} ${date.day}` : month;
}

/** "October 1, 2006" — APA's in-text dates and Chicago's dates. Empty without a date. */
export function longDate(date: PartialDate | null): string {
  if (!date) {
    return "";
  }
  const head = monthDay(date);
  if (head === "") {
    return String(date.year);
  }
  return date.day ? `${head}, ${date.year}` : `${head} ${date.year}`;
}

/** APA's date range in a reference: "2015–2016", or "2019" for a one-year run. */
export function apaYearRange(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "n.d.";
  }
  if (!end || end.year === start.year) {
    return String(start.year);
  }
  return `${start.year}–${end.year}`;
}

/**
 * MLA's date range, with the hyphen the MLA Style Center prints ("12 Mar.-4 Dec.
 * 2016", "15 Oct. 2016-Aug. 2017"): the first year is dropped when both dates fall in
 * the same year.
 */
export function mlaDateRange(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "";
  }
  const startHead = dayMonth(start, MLA_MONTHS);
  if (!end) {
    return startHead ? `${startHead} ${start.year}` : String(start.year);
  }
  const endHead = dayMonth(end, MLA_MONTHS);
  const endText = endHead ? `${endHead} ${end.year}` : String(end.year);
  if (startHead === "") {
    return end.year === start.year
      ? String(start.year)
      : `${start.year}-${end.year}`;
  }
  const startText =
    end.year === start.year ? startHead : `${startHead} ${start.year}`;
  return `${startText}-${endText}`;
}

/**
 * Chicago's date range, closed up with an en dash (the Chicago Style Q&A on citing an
 * exhibition asks for no spaces around it): "March 24–June 30, 2015", "October 26,
 * 2015–September 18, 2016".
 */
export function chicagoDateRange(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "";
  }
  if (!end) {
    return longDate(start);
  }
  if (!(start.month && end.month)) {
    return end.year === start.year
      ? String(start.year)
      : `${start.year}–${end.year}`;
  }
  if (end.year === start.year) {
    return `${monthDay(start)}–${longDate(end)}`;
  }
  return `${longDate(start)}–${longDate(end)}`;
}

/* ---------------------------------------------------------------- misc text */

/** The possessive in a credit line: "OpenAI’s", "Adobe’s" — 's, even after an s. */
export function possessive(name: string): string {
  return `${name.trim()}’s`;
}

const RE_COMPANY_SUFFIX = /,?\s+(inc\.?|llc|ltd\.?|corp\.?|co\.)$/i;

/** "Midjourney, Inc." → "Midjourney", for comparing a company with its tool. */
export function bareCompany(name: string): string {
  return name.trim().replace(RE_COMPANY_SUFFIX, "");
}

const RE_URL_SCHEME = /^https?:\/\//i;

/** A URL as typed, with "https://" added when the user left the scheme off. */
export function fullUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed === "" || RE_URL_SCHEME.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}
